<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import ConversationApi from 'dashboard/api/inbox/conversation';
import PhoneFilterModal from './PhoneFilterModal.vue';

const isLoading = ref(true);
const conversations = ref([]);
const totalCount = ref(0);

const currentPage = ref(1);
const searchQuery = ref('');
const activeFilters = ref({ type: [], status: [] });
const showFilterModal = ref(false);

const phoneConversations = computed(() => {
  let list = conversations.value.sort((a, b) => b.created_at - a.created_at);

  if (activeFilters.value.type.length > 0) {
    list = list.filter(chat => activeFilters.value.type.includes(getCallData(chat).type));
  }
  if (activeFilters.value.status.length > 0) {
    list = list.filter(chat => activeFilters.value.status.includes(getCallData(chat).status.toLowerCase()));
  }

  return list;
});


const fetchConversations = async (page = 1) => {
  try {
    isLoading.value = true;
    let payload = [];
    
    if (searchQuery.value.trim() !== '') {
      // Use global search and filter for phone channel
      const responseSearch = await ConversationApi.search({ q: searchQuery.value });
      const allMatches = responseSearch.data?.data?.payload || responseSearch.data?.payload || [];
      payload = allMatches.filter(chat => chat.meta?.channel === 'Channel::Api' || chat.channel_type === 'Channel::Api');
      totalCount.value = payload.length;
    } else {
      // Normal pagination
      const responseAll = await ConversationApi.get({ status: 'all', page, channelType: 'phone', assigneeType: 'all' });
      payload = responseAll.data?.data?.payload || [];
      totalCount.value = responseAll.data?.data?.meta?.all_count || 0;
    }

    const unique = [];
    const map = new Map();
    for (const item of payload) {
        if (!map.has(item.id)) {
            map.set(item.id, true);
            unique.push(item);
        }
    }
    conversations.value = unique;
    currentPage.value = page;
  } catch (error) {
    console.error('Failed to fetch phone history:', error);
  } finally {
    isLoading.value = false;
  }
};

let unsubscribe = null;
const store = useStore();

onMounted(async () => {
  await fetchConversations(currentPage.value);

  // Listen to Vuex actions triggered by WebSockets so the table updates in real-time
  unsubscribe = store.subscribeAction((action, state) => {
    if (['conversations/addMessage', 'conversations/addConversation', 'conversations/updateConversation'].includes(action.type)) {
      fetchConversations(currentPage.value);
    }
  });
});

const onSearch = () => {
  currentPage.value = 1;
  fetchConversations(1);
};

const nextPage = () => {
  if (phoneConversations.value.length === 25 || totalCount.value > currentPage.value * 25) {
    fetchConversations(currentPage.value + 1);
  }
};

const prevPage = () => {
  if (currentPage.value > 1) {
    fetchConversations(currentPage.value - 1);
  }
};

const applyFilters = (filters) => {
  activeFilters.value = filters;
  showFilterModal.value = false;
};

const downloadCSV = () => {
  const headers = ['Type', 'Status', 'From', 'To', 'Via Number', 'Created', 'Ended', 'Time'];
  const rows = phoneConversations.value.map(chat => {
    const data = getCallData(chat);
    return [
      data.type,
      (data.status || 'COMPLETED').toUpperCase(),
      data.from,
      data.to,
      data.via,
      formatDate(chat.created_at),
      formatDate(chat.timestamp),
      formatDuration(data.duration)
    ];
  });
  
  const csvContent = [
    headers.join(','),
    ...rows.map(e => e.map(field => `"${(field || '').toString().replace(/"/g, '""')}"`).join(','))
  ].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `phone_history_${new Date().toISOString()}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

onBeforeUnmount(() => {
  if (unsubscribe) unsubscribe();
});


const getCallData = (chat) => {
  let type = 'unknown';
  let from = 'Unknown';
  let to = 'Unknown';
  let duration = chat.custom_attributes?.call_duration_seconds || 0;
  let status = chat.custom_attributes?.call_status || 'unknown';

  // Extract all message text for smart regex parsing
  const allMessages = (chat.messages || []).map(m => m.content || '').join('\n');
  
  const contactNumber = chat.meta?.sender?.phone_number || 'Unknown';

  // 1. Infer Type & To/From
  const isOutbound = /Dialing|Outbound call to|Call to \+/i.test(allMessages);
  const isInbound = /Inbound call from|Call from \+/i.test(allMessages);
  
  if (isOutbound) {
    type = 'outbound';
    from = 'System';
    to = contactNumber;
  } else if (isInbound) {
    type = 'inbound';
    from = contactNumber;
    to = 'System';
  } else if (chat.custom_attributes && chat.custom_attributes.call_direction) {
    type = chat.custom_attributes.call_direction === 'outgoing' ? 'outbound' : 'inbound';
    if (type === 'outbound') {
      from = 'System';
      to = contactNumber;
    } else {
      from = contactNumber;
      to = 'System';
    }
  } else {
    // Default assumption
    type = 'inbound';
    from = contactNumber;
    to = 'System';
  }

  // 2. Infer Status (e.g. from '**Call ended** - answered' or 'Call ended - MISSED')
  const endedMatch = allMessages.match(/Call ended(?:\*\*|)\s*-\s*([^\n]+)/i);
  if (endedMatch) {
    status = endedMatch[1].trim(); 
  }

  // 3. Infer Time / Duration (e.g. from '* Total: 48s' or 'Total: 12s')
  const durationMatch = allMessages.match(/Total:\s*([^\n]+)/i);
  if (durationMatch) {
    duration = durationMatch[1].trim(); 
  }

  // Fallback to native voice_call message (Twilio) if NO custom_attributes at all
  if (!chat.custom_attributes || (!chat.custom_attributes.call_status && status === 'unknown')) {
    const voiceMsg = chat.messages?.slice().reverse().find(m => m.content_type === 'voice_call');
    if (voiceMsg && voiceMsg.call) {
      type = voiceMsg.call.direction === 'outgoing' ? 'outbound' : 'inbound';
      status = voiceMsg.call.status;
      duration = voiceMsg.call.duration_in_seconds || 0;
      if (type === 'outbound') {
        from = 'System';
        to = contactNumber;
      } else {
        from = contactNumber;
        to = 'System';
      }
    }
  }

  return {
    type,
    status,
    duration,
    from,
    to,
    via: chat.meta?.assignee?.name || chat.meta?.sender?.name || 'Not set',
  };
};

const formatDuration = (seconds) => {
  // If we regex'd a pre-formatted string like "48s" or "1m 5s", return it directly
  if (typeof seconds === 'string' && (seconds.includes('s') || seconds.includes('m'))) return seconds;
  if (!seconds || seconds <= 0) return '0s';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const formatDate = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp * 1000);
  return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`;
};

const getStatusColor = (status) => {
  const s = (status || 'completed').toLowerCase();
  if (s === 'answered' || s === 'completed') return 'bg-[#21ba45] text-white';
  if (s === 'missed' || s === 'failed') return 'bg-[#db2828] text-white';
  if (s === 'voicemail') return 'bg-[#f2711c] text-white';
  return 'bg-[#767676] text-white';
};
</script>

<template>
  <div class="flex flex-col h-full bg-n-background w-full overflow-hidden">
    <!-- Header -->
    <div class="flex flex-col border-b border-n-slate-3 bg-white dark:bg-n-solid-1">
      <div class="flex items-center justify-between p-6">
        <h1 class="text-2xl font-semibold text-n-slate-12">Phone / Call History</h1>
      </div>
      
      <!-- Actions Bar -->
      <div class="flex items-center justify-between px-6 py-3 border-t border-n-slate-3 bg-n-slate-1 dark:bg-n-solid-2">
        <div class="flex items-center gap-4">
          <button @click="showFilterModal = true" class="flex items-center gap-2 text-sm font-medium text-n-slate-11 hover:text-[#1f93ff] transition-colors">
            <span class="i-ph-faders"></span>
            CUSTOMIZE FILTER
          </button>
        </div>
        
        <div class="flex items-center gap-4">
          <div class="relative flex items-center">
            <span class="i-ph-magnifying-glass absolute left-3 text-n-slate-10"></span>
            <input 
              v-model="searchQuery" 
              @keyup.enter="onSearch"
              type="text" 
              placeholder="Search ..." 
              class="pl-9 pr-4 py-1.5 text-sm border border-n-slate-4 rounded bg-white dark:bg-n-solid-1 focus:ring-1 focus:ring-[#1f93ff] focus:border-[#1f93ff] outline-none w-64 text-n-slate-12 placeholder-n-slate-10"
            >
          </div>
          <button @click="downloadCSV" class="flex items-center gap-2 px-3 py-1.5 text-sm font-medium border border-n-slate-4 bg-white dark:bg-n-solid-1 hover:bg-n-slate-2 text-n-slate-11 rounded transition-colors shadow-sm">
            <span class="i-ph-export"></span>
            Export to CSV
          </button>
          
          <div class="flex items-center gap-3 ml-4 border-l border-n-slate-3 pl-4">
            <span class="text-sm text-n-slate-11 font-medium">
              Displaying {{ (currentPage - 1) * 25 + 1 }} - {{ Math.min(currentPage * 25, totalCount) }} of {{ totalCount }}
            </span>
            <button @click="fetchConversations(currentPage)" class="text-n-slate-11 hover:text-n-slate-12">
              <span class="i-ph-arrows-clockwise text-lg"></span>
            </button>
            <div class="flex items-center gap-1">
              <button @click="prevPage" :disabled="currentPage === 1" :class="['p-1 rounded transition-colors', currentPage === 1 ? 'text-n-slate-8 cursor-not-allowed' : 'text-n-slate-11 hover:bg-n-slate-3']">
                <span class="i-ph-caret-left text-lg flex"></span>
              </button>
              <button @click="nextPage" :disabled="phoneConversations.length < 25" :class="['p-1 rounded transition-colors', phoneConversations.length < 25 ? 'text-n-slate-8 cursor-not-allowed' : 'text-n-slate-11 hover:bg-n-slate-3']">
                <span class="i-ph-caret-right text-lg flex"></span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Table Container -->
    <div class="flex-1 overflow-auto p-6">
      <div v-if="isLoading" class="flex items-center justify-center h-64">
        <span class="text-n-slate-11">Loading call history...</span>
      </div>
      <div v-else class="bg-white dark:bg-n-solid-1 rounded-md shadow-sm border border-n-slate-3 overflow-hidden">
        <table class="min-w-full divide-y divide-n-slate-3">
          <thead class="bg-n-slate-1 dark:bg-n-solid-2">
            <tr>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-n-slate-10 uppercase tracking-wider whitespace-nowrap">Type</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-n-slate-10 uppercase tracking-wider whitespace-nowrap">Status</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-n-slate-10 uppercase tracking-wider whitespace-nowrap">From</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-n-slate-10 uppercase tracking-wider whitespace-nowrap">To</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-n-slate-10 uppercase tracking-wider whitespace-nowrap">Via number</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-n-slate-10 uppercase tracking-wider whitespace-nowrap">Created</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-n-slate-10 uppercase tracking-wider whitespace-nowrap">↓ Ended</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-n-slate-10 uppercase tracking-wider whitespace-nowrap">Time</th>
            </tr>
          </thead>
          <tbody class="bg-white dark:bg-n-solid-1 divide-y divide-n-slate-3">
            <tr v-for="chat in phoneConversations" :key="chat.id" class="hover:bg-n-slate-1 transition-colors">
              <td class="px-6 py-4 whitespace-nowrap">
                <span 
                  class="inline-flex items-center gap-1 px-2 py-1 text-xs font-bold uppercase rounded text-white shadow-sm"
                  :class="getCallData(chat).type === 'outbound' ? 'bg-[#2185d0]' : 'bg-[#21ba45]'"
                >
                  <span :class="getCallData(chat).type === 'outbound' ? 'i-ph-arrow-left' : 'i-ph-arrow-right'"></span>
                  {{ getCallData(chat).type === 'outbound' ? 'OUTGOING' : 'INCOMING' }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span 
                  class="inline-flex items-center gap-1 px-2 py-1 text-xs font-bold uppercase rounded shadow-sm"
                  :class="getStatusColor(getCallData(chat).status)"
                >
                  <span class="i-ph-phone-call"></span>
                  {{ (getCallData(chat).status || 'COMPLETED').toUpperCase() }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-n-ruby-11 font-medium">
                {{ getCallData(chat).from }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-n-ruby-11 font-medium">
                {{ getCallData(chat).to }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-n-slate-11">
                {{ getCallData(chat).via }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-n-slate-11">
                {{ formatDate(chat.created_at) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-n-slate-11">
                {{ formatDate(chat.timestamp) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-n-slate-11">
                {{ formatDuration(getCallData(chat).duration) }}
              </td>
            </tr>
            <tr v-if="phoneConversations.length === 0">
              <td colspan="8" class="px-6 py-8 text-center text-sm text-n-slate-11">
                No phone history found.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Filter Modal -->
    <PhoneFilterModal 
      :show="showFilterModal" 
      :activeFilters="activeFilters"
      @close="showFilterModal = false" 
      @apply="applyFilters" 
    />
  </div>
</template>
